import React, {useMemo, useState} from 'react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import LinkWithTooltip from '../Common/LinkWithTooltip.jsx';
import { getLocalDateFromUTCTimestamp } from '../../lib/common';

const columnHelper = createColumnHelper();

const naturalSort = (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);

    const isNumericA = !isNaN(a);
    const isNumericB = !isNaN(b);

    if (isNumericA && isNumericB) {
        return Number(a) - Number(b);
    }
    if (isNumericA) return -1;
    if (isNumericB) return 1;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const numericSort = (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);
    return Number(a) - Number(b);
};

export default function ReactTableWrapperProjects({
                                             projects,
                                             handleShow,
                                             handleShareShow,
                                             t,
                                         }) {
    // Pre-map data so table receives a clean, stable array
    const data = useMemo(
        () =>
            projects.map(project => ({
                id: project.id,
                name: project.name,
                reports: project.reports,
                author: project.author,
                created: getLocalDateFromUTCTimestamp(
                    project.createdTimestamp * 1000
                ),
                editable: project.editable,
                shared: project.shared,
            })),
        [projects]
    );

    // 📑 Define columns once (wrapped in useMemo to avoid rebuild on rerenders)
    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: t('messages.item:Project'),
                sortingFn: naturalSort,
                size: 500,
                cell: info => (
                    <Link
                        to={{
                            pathname: '/admin/viewProject',
                            state: {
                                id: info.row.original.id,
                                projectName: info.row.original.name,
                                editable: info.row.original.editable,
                            },
                        }}
                        className='NoDecLink'
                    >
                        {info.getValue()}
                    </Link>
                ),
            }),
            columnHelper.accessor('reports', {
                header: t('project:reports'),
                sortingFn: numericSort,
                size: 150
            }),
            columnHelper.accessor('author', {
                header: t('project:author'),
                sortingFn: naturalSort,
                size: 300
            }),
            columnHelper.accessor('created', {
                header: t('project:created'),
                size: 200,
                sortingFn: (rowA, rowB, columnId) =>
                    new Date(rowA.getValue(columnId)) - new Date(rowB.getValue(columnId)),
            }),
            columnHelper.display({
                id: 'actions',
                header: t('user:actions'),
                size: 150,
                enableSorting: false,
                cell: info => {
                    const { id, name, author, editable, shared } = info.row.original;
                    return editable ? (
                        <div className="actions-right">
                            <div className="action-icon-wrapper">
                                <LinkWithTooltip
                                    tooltip={t('common:share')}
                                    onClick={handleShareShow}
                                    href="projects"
                                    id={`${id}_-$-_${name}_-$-_${author}_-$-_${shared}_-$-_share`}
                                    tooltipIconClass="fa fa-user"
                                    iconStyle={{ fontSize: '16px' }}
                                />
                            </div>
                            <div className="action-icon-wrapper">
                                <LinkWithTooltip
                                    tooltip={t('common:edit')}
                                    href="editProject"
                                    id={id}
                                    tooltipIconClass="fa fa-edit"
                                    iconStyle={{ fontSize: '16px' }}
                                />
                            </div>
                            <div className="action-icon-wrapper">
                                <LinkWithTooltip
                                    tooltip={t('common:delete')}
                                    onClick={handleShow}
                                    href="projects"
                                    id={`${id}_-$-_${name}_-$-_${author}_-$-_${shared}_-$-_delete`}
                                    tooltipIconClass="fa fa-times"
                                    iconStyle={{ fontSize: '16px' }}
                                />
                            </div>
                        </div>
                    ) : null;
                },
            }),
        ],
        [t, handleShow, handleShareShow]
    );

    const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);

    // Uncontrolled table state — no risk of recursion
    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        enableMultiSort: true,
        initialState: {
            pagination: { pageSize: 10 },
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <>
            <div className="table-responsive">
                <table className="table table-hover -highlight">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        onClick={() => {
                                            if (!header.column.getCanSort()) return;

                                            const isSorted = header.column.getIsSorted();
                                            const id = header.column.id;

                                            setSorting([{ id, desc: isSorted === 'asc' }]); // Asc ↔ Desc only
                                        }}
                                        style={{
                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanSort() && (
                                            <span className={`sort-icon-stack ${header.column.getIsSorted() ? 'sorted' : ''}`}>
                                                {header.column.getIsSorted() === 'asc' && (
                                                    <i className="fa fa-sort-up" />
                                                )}
                                                {header.column.getIsSorted() === 'desc' && (
                                                    <i className="fa fa-sort-down" />
                                                )}
                                                {header.column.getIsSorted() === false && (
                                                    <>
                                                        <i className="fa fa-sort-up" />
                                                        <i className="fa fa-sort-down" />
                                                    </>
                                                )}
                                            </span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length}>{t('project:noPrjectsFound')}</td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pagination-controls">
                <button className="page-button pb-s" onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>
                    {'<<'}
                </button>
                <button className="page-button pb-l" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    {t('common:previous')}
                </button>
                <span className="flex items-center gap-1 page-span">
                    {t('common:page')}{' '}
                    <input
                        type="number"
                        min={1}
                        max={table.getPageCount()}
                        value={table.getState().pagination.pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0
                            table.setPageIndex(page)
                        }}
                        className="page-input"
                    />{' '}
                    {t('common:of')} {table.getPageCount()}
                </span>
                <button className="page-button pb-l" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    {t('common:next')}
                </button>
                <button className="page-button pb-s" onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>
                    {'>>'}
                </button>
                <select
                    className="page-select"
                    style={{ fontSize: '12px' }}
                    value={table.getState().pagination.pageSize}
                    onChange={e => table.setPageSize(Number(e.target.value))}
                >
                    {[10, 25, 50].map(size => (
                        <option key={size} value={size}>
                            {size} Rows
                        </option>
                    ))}
                </select>
            </div>
        </>
    );
}